/**
 * Chapter011.js — Training & Competency Register (OHSE 011)
 * Migrated from SWMS Platform Chapter011.tsx → JavaScript
 *
 * Props:
 *   data       {array}   training_entries from swmsData
 *   onChange   {fn}      (newEntries) => void
 *   companyName {string} from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

export default function Chapter011({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => {
    onChange([...data, {
      worker_name: '',
      role: '',
      induction_date: '',
      ticket_type: '',
      ticket_number: '',
      expiry_date: '',
    }]);
  }, [data, onChange]);

  const updateEntry = useCallback((idx, field, value) => {
    const next = [...data];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  }, [data, onChange]);

  const removeEntry = useCallback((idx) => {
    onChange(data.filter((_, i) => i !== idx));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 011</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Training &amp; Competency Register
        </h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed space-y-2">
        <p>
          Having regard to the hazards and risks associated with the work activity,{' '}
          <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
          has assured that all employees are trained and competent to perform all tasks in a way
          that is safe and does not adversely impact on themselves, others or the environment.
        </p>
        <p>The following register contains details of the skills and competencies of the organisation's employees.</p>
      </div>

      {/* Worker Records */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Worker Training Records</h3>
          </div>
          {data.length > 0 && (
            <div className="px-3 py-1 bg-white border border-[#e0dbd0] rounded-md">
              <span className="text-xs text-gray-500">Total Workers: </span>
              <span className="text-sm font-semibold text-[#1a1a2e]">{data.length}</span>
            </div>
          )}
        </div>

        <div className="p-4">
          {data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-400 mb-3">No training records yet.</p>
              <button type="button" onClick={addEntry}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
                <Plus className="w-4 h-4" /> Add Worker
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {data.map((entry, idx) => (
                  <WorkerCard
                    key={idx}
                    entry={entry}
                    index={idx}
                    onUpdate={(field, value) => updateEntry(idx, field, value)}
                    onRemove={() => removeEntry(idx)}
                  />
                ))}
              </div>
              <div className="flex justify-center mt-4">
                <button type="button" onClick={addEntry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
                  <Plus className="w-4 h-4" /> Add Worker
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Worker card ──────────────────────────────────────────────────────────────

function WorkerCard({ entry, index, onUpdate, onRemove }) {
  return (
    <div className="border border-[#e0dbd0] rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-[#1a1a2e]">
            {entry.worker_name || 'New Worker'}
          </span>
          {entry.role && (
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium border border-blue-200">
              {entry.role}
            </span>
          )}
        </div>
        <button type="button" onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Worker Name *">
          <input type="text" value={entry.worker_name || ''} placeholder="Full name"
            onChange={e => onUpdate('worker_name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Role">
          <input type="text" value={entry.role || ''} placeholder="e.g. Carpenter, Electrician"
            onChange={e => onUpdate('role', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Induction Date">
          <input type="date" value={entry.induction_date || ''}
            onChange={e => onUpdate('induction_date', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Ticket / Licence Type">
          <input type="text" value={entry.ticket_type || ''} placeholder="e.g. White Card, EWP"
            onChange={e => onUpdate('ticket_type', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Ticket / Licence Number">
          <input type="text" value={entry.ticket_number || ''} placeholder="Licence number"
            onChange={e => onUpdate('ticket_number', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Expiry Date">
          <input type="date" value={entry.expiry_date || ''}
            onChange={e => onUpdate('expiry_date', e.target.value)} className={inputCls} />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
